import { type ApiError, useApi } from '@omnipresentgroup/api';
import { HTTPStatusCodes } from '@omnipresentgroup/constants';
import { notifyWarning } from '@omnipresentgroup/design-system';
import type {
  CompensationSource,
  GetCompensationVariantPaymentsResponse,
  GetCompensationVariantPaymentsResponseAPI,
} from '@omnipresentgroup/types';
import { useQuery } from '@tanstack/react-query';

import { API_BASE, VARIANT_PAYMENTS_API_PATH } from '../constants';
import { transformCompensationVariantPaymentsRequestFromAPI } from '../mappers';

const RETRY_FAILURE_COUNT = 60;
const RETRY_INTERVAL = 1000;

export const useCompensationVariantPayments = ({
  employeeId,
  source,
}: {
  employeeId?: string;
  source?: CompensationSource;
}) => {
  const { GET } = useApi();

  return useQuery<
    GetCompensationVariantPaymentsResponseAPI,
    ApiError,
    GetCompensationVariantPaymentsResponse
  >({
    queryKey: [VARIANT_PAYMENTS_API_PATH, employeeId, source],
    queryFn: () =>
      GET<GetCompensationVariantPaymentsResponseAPI>(
        `${API_BASE}/${VARIANT_PAYMENTS_API_PATH}`,
        {},
        {
          ...(employeeId ? { employee_id: employeeId } : {}),
          ...(source ? { source: source } : {}),
        },
      ),
    select: transformCompensationVariantPaymentsRequestFromAPI,
    retry: (failureCount, error) => {
      if (error.status === HTTPStatusCodes.NOT_FOUND) {
        // eslint-disable-next-line no-magic-numbers
        failureCount === 1 &&
          notifyWarning({
            title: 'Loading compensation items',
            description: 'This may take a moment.',
          });
        return failureCount <= RETRY_FAILURE_COUNT;
      }
      return false;
    },
    retryDelay: RETRY_INTERVAL,
  });
};
